import { useState, useRef } from 'react';
import { Upload, X } from 'lucide-react';

interface LogoUploadProps {
  onFileSelected: (file: File | null) => void;
  currentFile: File | null;
  // Review fix, round 1: the Task 15 coral restyle dropped this component's
  // own "SHOP LOGO" label on the assumption every caller supplies its own
  // heading — true for StorefrontSettings.tsx (has a "Shop logo" <h2> right
  // above it) but NOT for ShopRegistration.tsx, which mounts LogoUpload with
  // no heading of its own and was left with an unlabeled drop-zone. Defaults
  // to true (label shown) so every caller that doesn't explicitly opt out
  // keeps one; StorefrontSettings passes false.
  showLabel?: boolean;
}

export default function LogoUpload({ onFileSelected, currentFile, showLabel = true }: LogoUploadProps) {
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setError('PNG, JPG, OR WEBP ONLY');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('MAX FILE SIZE: 2MB');
      return;
    }

    setError(null);
    onFileSelected(file);

    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  }

  function handleRemove() {
    setPreview(null);
    setError(null);
    onFileSelected(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div>
      {showLabel && (
        <label className="font-heading text-[8px] text-body-grey uppercase block mb-2">
          Shop logo
        </label>
      )}
      {preview && currentFile ? (
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-[10px] border-[3px] border-primary bg-accent-alt overflow-hidden shrink-0">
            <img
              src={preview}
              alt="Logo preview"
              className="w-full h-full object-contain"
              style={{ imageRendering: 'pixelated' }}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <p className="font-heading text-[8px] text-success">LOGO SELECTED</p>
            <p className="font-grotesk font-medium text-[12px] text-body-grey">
              {currentFile?.name} ({Math.round((currentFile?.size || 0) / 1024)}KB)
            </p>
            <button
              type="button"
              onClick={handleRemove}
              className="font-grotesk font-bold text-[12px] text-error flex items-center gap-1 py-1 -my-1 w-fit"
            >
              <X size={12} /> Remove
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          className="w-full p-6 rounded-[10px] border-[3px] border-dashed border-hairline hover:border-coral transition-colors cursor-pointer text-center"
        >
          <Upload size={22} className="mx-auto mb-2 text-body-grey" />
          <p className="font-grotesk font-bold text-[13px] text-body-grey">
            Drop logo or click to upload
          </p>
          <p className="font-heading text-[7px] text-body-grey mt-1.5">
            PNG, JPG, WEBP — MAX 2MB
          </p>
        </div>
      )}

      {error && (
        <p className="font-heading text-[8px] text-error mt-1.5">{error}</p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
}
